/**
 * Capacity Service API Contracts
 * Request/Response schemas for the capacity service
 *
 * The capacity service enforces the 25,000 concurrent visitor hard limit
 * and provides real-time occupancy data for the park and individual zones.
 */

import { z } from 'zod';

import { BooleanFromString } from '../common';

// ============================================================================
// Capacity Status
// ============================================================================

/**
 * Park or zone capacity status levels.
 * - LOW: 0-50% occupancy
 * - MODERATE: 50-70% occupancy
 * - HIGH: 70-85% occupancy
 * - CRITICAL: 85-95% occupancy (triggers dynamic pricing increase)
 * - FULL: 95-100% occupancy (new entries denied)
 */
export const CapacityStatusSchema = z.enum(['LOW', 'MODERATE', 'HIGH', 'CRITICAL', 'FULL']);
export type CapacityStatus = z.infer<typeof CapacityStatusSchema>;

// ============================================================================
// Park Capacity
// ============================================================================

/** Real-time park-wide occupancy information */
export const ParkCapacitySchema = z.object({
  /** Number of visitors currently inside the park */
  currentOccupancy: z.number().int().nonnegative(),
  /** Hard limit for concurrent visitors (default: 25,000) */
  maxCapacity: z.number().int().positive(),
  /** Current occupancy as a percentage of max capacity (0-100, may include decimals e.g. 73.4) */
  occupancyPercentage: z.number().min(0).max(100),
  /** Computed status based on occupancy thresholds */
  status: CapacityStatusSchema,
  /** ISO 8601 timestamp of last occupancy update */
  lastUpdated: z.string().datetime(),
});

export type ParkCapacity = z.infer<typeof ParkCapacitySchema>;

// ============================================================================
// Zone Capacity
// ============================================================================

/** Real-time zone-level occupancy information */
export const ZoneCapacitySchema = z.object({
  /** Unique zone identifier */
  zoneId: z.string(),
  /** Human-readable zone name */
  zoneName: z.string(),
  /** Number of visitors currently in this zone */
  currentOccupancy: z.number().int().nonnegative(),
  /** Maximum capacity for this zone */
  maxCapacity: z.number().int().positive(),
  /** Current occupancy as a percentage (0-100, may include decimals) */
  occupancyPercentage: z.number().min(0).max(100),
  /** Computed status based on zone occupancy thresholds */
  status: CapacityStatusSchema,
});

export type ZoneCapacity = z.infer<typeof ZoneCapacitySchema>;

// ============================================================================
// Zone Management
// ============================================================================

/** Zone configuration record */
export const ZoneSchema = z.object({
  id: z.string(),
  name: z.string(),
  code: z.string().optional(),
  description: z.string().nullable(),
  location: z.string().nullable().optional(),
  /** Maximum number of visitors allowed in this zone concurrently */
  maxCapacity: z.number().int().positive(),
  // ticketing-service Attraction.id (kind=ZONE) this zone represents in the
  // admin attraction UI. Nullable: capacity-service zones predate this link
  // and may not correspond 1:1 with an Attraction row.
  attractionId: z.string().uuid().nullable().optional(),
  // Whether entry is actually blocked once maxCapacity is reached. Defaults
  // false — there's no zone-exit tracking wired into the live scan path yet
  // (see docs/design/features/zone-capacity-linking.md), so this is
  // admin-configurable but not yet enforced at the gate.
  capacityEnforced: z.boolean().optional(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Zone = z.infer<typeof ZoneSchema>;

/**
 * Zone record enriched with live occupancy (the shape actually returned by
 * GET /admin/capacity/zones and GET /capacity/zones/:zoneId — see
 * ZonesService.enrichZonesWithOccupancy).
 */
export const ZoneWithOccupancySchema = ZoneSchema.extend({
  currentOccupancy: z.number().int().nonnegative(),
  occupancyPercentage: z.number().min(0).max(100),
  status: CapacityStatusSchema,
});

export type ZoneWithOccupancy = z.infer<typeof ZoneWithOccupancySchema>;

/** Request to create a new zone */
export const CreateZoneRequestSchema = z.object({
  name: z.string().min(1).max(100),
  // Matches ticketing-service's Attraction.code max (#743 review) — zones
  // auto-created from an attraction (edit-attraction-dialog.tsx) reuse the
  // attraction's code as-is, so this must be at least as long.
  code: z.string().min(1).max(50).regex(/^[A-Z0-9_-]+$/),
  description: z.string().max(500).optional(),
  maxCapacity: z.number().int().positive(),
  location: z.string().max(200).optional(),
  attractionId: z.string().uuid().optional(),
  capacityEnforced: z.boolean().optional(),
  isActive: z.boolean().optional(),
});

export type CreateZoneRequest = z.infer<typeof CreateZoneRequestSchema>;

/** Request to update an existing zone */
export const UpdateZoneRequestSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  code: z.string().min(1).max(50).regex(/^[A-Z0-9_-]+$/).optional(),
  description: z.string().max(500).optional(),
  maxCapacity: z.number().int().positive().optional(),
  location: z.string().max(200).optional(),
  // Nullable (not just optional) so the admin dialog can explicitly unlink a
  // zone from an attraction, not just skip touching the field.
  attractionId: z.string().uuid().nullable().optional(),
  capacityEnforced: z.boolean().optional(),
  isActive: z.boolean().optional(),
});

export type UpdateZoneRequest = z.infer<typeof UpdateZoneRequestSchema>;

/** Mode for POST /admin/capacity/zones/:zoneId/occupancy/reset */
export const ZoneOccupancyResetModeSchema = z.enum(['RECONCILE', 'ZERO']);

/** Request to manually correct a zone's live occupancy counter */
export const ResetZoneOccupancyRequestSchema = z.object({
  mode: ZoneOccupancyResetModeSchema,
  // Required when mode=RECONCILE (the counter is set to this value); ignored
  // for mode=ZERO.
  actualCount: z.number().int().nonnegative().optional(),
}).superRefine((data, ctx) => {
  if (data.mode === 'RECONCILE' && data.actualCount === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'actualCount is required when mode is RECONCILE',
      path: ['actualCount'],
    });
  }
});

export type ResetZoneOccupancyRequest = z.infer<typeof ResetZoneOccupancyRequestSchema>;

// ============================================================================
// Occupancy Events
// ============================================================================

/** Types of occupancy-changing events */
export const OccupancyEventTypeSchema = z.enum(['ENTRY', 'EXIT', 'ZONE_ENTRY', 'ZONE_EXIT']);
export type OccupancyEventType = z.infer<typeof OccupancyEventTypeSchema>;

/** Request to record a park entry via gate */
export const RecordEntryRequestSchema = z.object({
  /** UUID of the visitor's wristband */
  wristbandId: z.string().uuid(),
  /** ID of the entry gate */
  gateId: z.string(),
});

export type RecordEntryRequest = z.infer<typeof RecordEntryRequestSchema>;

/** Request to record a park exit via gate */
export const RecordExitRequestSchema = z.object({
  wristbandId: z.string().uuid(),
  gateId: z.string(),
});

export type RecordExitRequest = z.infer<typeof RecordExitRequestSchema>;

/** Request to record entry into a specific zone */
export const RecordZoneEntryRequestSchema = z.object({
  wristbandId: z.string().uuid(),
  zoneId: z.string(),
});

export type RecordZoneEntryRequest = z.infer<typeof RecordZoneEntryRequestSchema>;

/** Request to record exit from a specific zone */
export const RecordZoneExitRequestSchema = z.object({
  wristbandId: z.string().uuid(),
  zoneId: z.string(),
});

export type RecordZoneExitRequest = z.infer<typeof RecordZoneExitRequestSchema>;

// ============================================================================
// Capacity Snapshots
// ============================================================================

/** Point-in-time snapshot of park and zone occupancy (stored for analytics) */
export const CapacitySnapshotSchema = z.object({
  id: z.string(),
  /** Park-wide visitor count at snapshot time */
  parkOccupancy: z.number().int().nonnegative(),
  /** Park max capacity at snapshot time */
  parkMaxCapacity: z.number().int().positive(),
  /** Park occupancy as a percentage (0-100, may include decimals) */
  parkPercentage: z.number().min(0).max(100),
  /** Per-zone occupancy data at snapshot time */
  zoneData: z.array(ZoneCapacitySchema),
  /** ISO 8601 timestamp when snapshot was taken */
  timestamp: z.string().datetime(),
});

export type CapacitySnapshot = z.infer<typeof CapacitySnapshotSchema>;

/** Query parameters for retrieving capacity snapshots */
export const GetSnapshotsQuerySchema = z.object({
  /** ISO datetime lower bound (inclusive) */
  startTime: z.string().datetime().optional(),
  /** ISO datetime upper bound (inclusive) */
  endTime: z.string().datetime().optional(),
  /** Max snapshots to return (1-1000, default 100) */
  limit: z.coerce.number().int().positive().max(1000).default(100),
});

export type GetSnapshotsQuery = z.infer<typeof GetSnapshotsQuerySchema>;

// ============================================================================
// Capacity Check Response
// ============================================================================

/** Response from the capacity availability check endpoint */
export const HasCapacityResponseSchema = z.object({
  /** Whether the park can accept new visitors */
  hasCapacity: z.boolean(),
  /** Current number of visitors in the park */
  currentOccupancy: z.number().int().nonnegative(),
  /** Hard limit for concurrent visitors */
  maxCapacity: z.number().int().positive(),
  /** Human-readable status message */
  message: z.string().optional(),
});

export type HasCapacityResponse = z.infer<typeof HasCapacityResponseSchema>;

// ============================================================================
// Attraction Time Slots (P3-ATT-003)
// Migrated from ticketing-service to capacity-service.
// ============================================================================

export const AttractionTimeSlotStatusSchema = z.enum(['AVAILABLE', 'LIMITED', 'SOLD_OUT']);
export type AttractionTimeSlotStatus = z.infer<typeof AttractionTimeSlotStatusSchema>;

/** Public slot response — includes virtual `available` and `status` fields */
export const AttractionTimeSlotSchema = z.object({
  id: z.string(),
  attractionId: z.string(),
  /** WIB date (YYYY-MM-DD) */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** Start time HH:mm in WIB */
  startTime: z.string(),
  /** End time HH:mm in WIB */
  endTime: z.string(),
  capacity: z.number().int().nonnegative(),
  reserved: z.number().int().nonnegative(),
  /** Derived: capacity - reserved */
  available: z.number().int().nonnegative(),
  status: AttractionTimeSlotStatusSchema,
  isActive: z.boolean(),
});

export type AttractionTimeSlot = z.infer<typeof AttractionTimeSlotSchema>;

// ============================================================================
// Batch Slot Query (P3-ATT-003 extension)
// Fetches slots for multiple attractions in a single request, used by the
// group booking wizard time grid to avoid N serial HTTP calls.
// ============================================================================

/** Query parameters for batch slot fetch */
export const BatchSlotsQuerySchema = z.object({
  /** WIB date (YYYY-MM-DD) */
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** Comma-separated list of attraction IDs */
  attractionIds: z.string().min(1),
  /** Group size used to compute per-slot status (AVAILABLE if available >= groupSize) */
  groupSize: z.coerce.number().int().min(1).optional(),
});

export type BatchSlotsQuery = z.infer<typeof BatchSlotsQuerySchema>;

/** Response: map of attractionId → slots array, plus blackout attractions */
export const BatchSlotsResponseSchema = z.object({
  date: z.string(),
  groupSize: z.number().optional(),
  /** Key: attractionId, Value: ordered list of time slots for that attraction on the date */
  slots: z.record(z.array(AttractionTimeSlotSchema)),
  /** Attraction IDs that are intentionally closed (CLOSED blackout) on this date */
  blackoutAttractions: z.array(z.string()).default([]),
});

export type BatchSlotsResponse = z.infer<typeof BatchSlotsResponseSchema>;

/** Response for GET /api/v1/capacity/attractions/:attractionId/slots (single attraction) */
export const AttractionSlotsResponseSchema = z.object({
  slots: z.array(AttractionTimeSlotSchema),
  /** true = intentionally CLOSED blackout date; false = slots not yet generated */
  blackout: z.boolean(),
});

export type AttractionSlotsResponse = z.infer<typeof AttractionSlotsResponseSchema>;

export const AttractionReservationStatusSchema = z.enum([
  'ACTIVE',
  'REDEEMED',
  'CANCELLED',
  'NO_SHOW',
]);
export type AttractionReservationStatus = z.infer<typeof AttractionReservationStatusSchema>;

/** Individual customer wristband reservation at an attraction slot */
export const AttractionReservationSchema = z.object({
  id: z.string(),
  slotId: z.string(),
  attractionId: z.string().optional(),
  attractionName: z.string().optional(),
  customerId: z.string(),
  wristbandId: z.string(),
  status: AttractionReservationStatusSchema,
  reservedAt: z.string().datetime(),
  redeemedAt: z.string().datetime().nullable().optional(),
  cancelledAt: z.string().datetime().nullable().optional(),
  noShowAt: z.string().datetime().nullable().optional(),
  slot: z.lazy(() => AttractionTimeSlotSchema.optional()),
});

export type AttractionReservation = z.infer<typeof AttractionReservationSchema>;

// ============================================================================
// Slot Reservation Errors (structured 409 for order creation)
// ============================================================================

/**
 * Machine-readable reason a slot reservation failed during order creation.
 * Lets the customer-facing client render a slot-specific, actionable message
 * instead of a generic error.
 *
 * - SLOT_SOLD_OUT: the slot has no remaining capacity (sold out mid-checkout)
 * - INSUFFICIENT_CAPACITY: some seats remain but fewer than requested
 * - SLOT_NOT_FOUND: the slot id is invalid/expired or the schedule was regenerated
 * - VISITOR_TYPE_CAP_EXCEEDED: a visitor-type sub-cap is full (e.g. CHILD quota)
 */
export const SlotReservationReasonSchema = z.enum([
  'SLOT_SOLD_OUT',
  'INSUFFICIENT_CAPACITY',
  'SLOT_NOT_FOUND',
  'VISITOR_TYPE_CAP_EXCEEDED',
]);
export type SlotReservationReason = z.infer<typeof SlotReservationReasonSchema>;

/** Identifies the slot that failed and the numbers behind the failure. */
export const SlotReservationErrorSlotSchema = z.object({
  slotId: z.string(),
  attractionId: z.string(),
  /** Visit date, YYYY-MM-DD */
  date: z.string(),
  /** Slot window start, HH:mm */
  startTime: z.string(),
  /** Slot window end, HH:mm */
  endTime: z.string(),
  /** Seats remaining at the time of failure (omitted for SLOT_NOT_FOUND) */
  available: z.number().int().nonnegative().optional(),
  /** Seats the order asked for */
  requested: z.number().int().positive().optional(),
  /** Visitor type for VISITOR_TYPE_CAP_EXCEEDED */
  visitorType: z.string().optional(),
});
export type SlotReservationErrorSlot = z.infer<typeof SlotReservationErrorSlotSchema>;

/** Stable error code used across services for a failed slot reservation. */
export const SLOT_RESERVATION_FAILED_CODE = 'SLOT_RESERVATION_FAILED' as const;

/**
 * The failure payload: which slot failed and why. This is what travels in the
 * API error envelope's `details` field, and what the web checkout parses to
 * build a slot-specific message.
 */
export const SlotReservationFailureSchema = z.object({
  reason: SlotReservationReasonSchema,
  slot: SlotReservationErrorSlotSchema,
});
export type SlotReservationFailure = z.infer<typeof SlotReservationFailureSchema>;

/**
 * Flat 409 body thrown by capacity-service (which has no error-envelope filter,
 * so the object is serialized as-is). ticketing-service parses this, then
 * re-emits it through its envelope filter as { code, details }.
 */
export const SlotReservationErrorSchema = SlotReservationFailureSchema.extend({
  code: z.literal(SLOT_RESERVATION_FAILED_CODE),
});
export type SlotReservationError = z.infer<typeof SlotReservationErrorSchema>;

// ============================================================================
// Daily Capacity Configuration (Admin)
// ============================================================================

export const DailyCapacityRecordSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  totalCapacity: z.number().int().positive(),
  onlineAllocationPercent: z.number().int().min(0).max(100),
  walkupAllocationPercent: z.number().int().min(0).max(100),
  currentBookings: z.number().int().nonnegative(),
  onlineBookings: z.number().int().nonnegative(),
  walkupBookings: z.number().int().nonnegative(),
  isBlocked: z.boolean(),
  blockReason: z.string().optional(),
});

export type DailyCapacityRecord = z.infer<typeof DailyCapacityRecordSchema>;

export const MonthCapacityResponseSchema = z.object({
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
  days: z.array(DailyCapacityRecordSchema),
});

export type MonthCapacityResponse = z.infer<typeof MonthCapacityResponseSchema>;

export const GetCapacityConfigQuerySchema = z.object({
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export type GetCapacityConfigQuery = z.infer<typeof GetCapacityConfigQuerySchema>;

export const UpdateDailyCapacitySchema = z.object({
  totalCapacity: z.number().int().min(0).max(25000),
  onlineAllocationPercent: z.number().int().min(0).max(100).optional(),
  reason: z.string().optional(),
});

export type UpdateDailyCapacity = z.infer<typeof UpdateDailyCapacitySchema>;

export const BulkUpdateCapacitySchema = z.object({
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  totalCapacity: z.number().int().min(0).max(25000),
  onlineAllocationPercent: z.number().int().min(0).max(100).optional(),
  walkupAllocationPercent: z.number().int().min(0).max(100).optional(),
});

export type BulkUpdateCapacity = z.infer<typeof BulkUpdateCapacitySchema>;

// ============================================================================
// Blocked Dates (Admin)
// ============================================================================

export const BlockDateSchema = z.object({
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().min(1).max(500),
  displayToCustomers: z.boolean().optional().default(true),
}).refine((data) => data.dateTo >= data.dateFrom, {
  path: ['dateTo'],
  message: 'dateTo must be on or after dateFrom',
});

export type BlockDate = z.infer<typeof BlockDateSchema>;

export const BlockedDateRecordSchema = z.object({
  id: z.string(),
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string(),
  displayToCustomers: z.boolean(),
  createdBy: z.string(),
  createdAt: z.string().datetime(),
  existingOrdersCount: z.number().int().nonnegative().optional(),
});

export type BlockedDateRecord = z.infer<typeof BlockedDateRecordSchema>;

/**
 * GET /api/v1/capacity/blocked-dates (public, customer-facing).
 * Deliberately narrower than BlockedDateRecordSchema -- the public endpoint
 * (capacity.controller.ts's getPublicBlockedDates) strips admin metadata
 * (id, displayToCustomers, createdBy, createdAt), returning only the fields
 * customer-facing date pickers need.
 */
export const PublicBlockedDateSchema = z.object({
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string(),
});

export type PublicBlockedDate = z.infer<typeof PublicBlockedDateSchema>;

export const BlockedDatesListResponseSchema = z.object({
  blockedDates: z.array(BlockedDateRecordSchema),
  total: z.number().int().nonnegative(),
});

export type BlockedDatesListResponse = z.infer<typeof BlockedDatesListResponseSchema>;

export const OrdersCheckResponseSchema = z.object({
  dateFrom: z.string(),
  dateTo: z.string(),
  orderCount: z.number().int().nonnegative(),
  hasOrders: z.boolean(),
});

export type OrdersCheckResponse = z.infer<typeof OrdersCheckResponseSchema>;

// ============================================================================
// Entry Attempt & Capacity Check
// ============================================================================

export const AttemptEntrySchema = z.object({
  wristbandId: z.string().min(1),
  gateId: z.string().min(1),
});

export type AttemptEntry = z.infer<typeof AttemptEntrySchema>;

export const AttemptZoneEntrySchema = z.object({
  wristbandId: z.string().min(1),
  zoneId: z.string().min(1),
});

export type AttemptZoneEntry = z.infer<typeof AttemptZoneEntrySchema>;

export const EntryAttemptResultSchema = z.object({
  allowed: z.boolean(),
  currentOccupancy: z.number().int(),
  maxCapacity: z.number().int().positive(),
  reason: z.string().optional(),
  message: z.string(),
});

export type EntryAttemptResult = z.infer<typeof EntryAttemptResultSchema>;

export const CapacityValidationStatusSchema = z.object({
  allowed: z.boolean(),
  currentOccupancy: z.number().int().nonnegative(),
  maxCapacity: z.number().int().positive(),
  status: z.string(),
});

export type CapacityValidationStatus = z.infer<typeof CapacityValidationStatusSchema>;

// ============================================================================
// Reservations (park-level capacity)
// ============================================================================

export const CreateReservationSchema = z.object({
  type: z.string(),
  referenceId: z.string(),
  visitDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  count: z.number().int().nonnegative(),
  expiresAt: z.string().datetime(),
});

export type CreateReservation = z.infer<typeof CreateReservationSchema>;

export const UpdateReservationCountSchema = z.object({
  count: z.number().int().min(0),
});

export type UpdateReservationCount = z.infer<typeof UpdateReservationCountSchema>;

// ============================================================================
// Capacity Pools (Admin)
// ============================================================================

export const CapacityPoolSchema = z.object({
  id: z.string(),
  poolName: z.string(),
  capacityPerSlot: z.number().int().positive(),
  attractionIds: z.array(z.string()),
  active: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type CapacityPool = z.infer<typeof CapacityPoolSchema>;

export const CreateCapacityPoolSchema = z.object({
  poolName: z.string().max(200),
  capacityPerSlot: z.number().int().positive(),
  attractionIds: z.array(z.string()),
});

export type CreateCapacityPool = z.infer<typeof CreateCapacityPoolSchema>;

export const UpdateCapacityPoolSchema = z.object({
  poolName: z.string().max(200).optional(),
  capacityPerSlot: z.number().int().positive().optional(),
  attractionIds: z.array(z.string()).optional(),
  active: z.boolean().optional(),
});

export type UpdateCapacityPool = z.infer<typeof UpdateCapacityPoolSchema>;

export const CapacityPoolListResponseSchema = z.object({
  pools: z.array(CapacityPoolSchema),
  total: z.number().int().nonnegative(),
});

export type CapacityPoolListResponse = z.infer<typeof CapacityPoolListResponseSchema>;

// ============================================================================
// Thresholds & Alerts (Admin)
// ============================================================================

export const ThresholdConfigSchema = z.object({
  name: z.string().max(50),
  percentage: z.number().int().min(1).max(100),
});

export type ThresholdConfig = z.infer<typeof ThresholdConfigSchema>;

export const UpdateThresholdsSchema = z.object({
  thresholds: z.array(ThresholdConfigSchema),
});

export type UpdateThresholds = z.infer<typeof UpdateThresholdsSchema>;

export const GetAlertsQuerySchema = z.object({
  page: z.coerce.number().int().min(1).optional().default(1),
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  acknowledged: BooleanFromString.optional(),
  level: z.string().optional(),
});

export type GetAlertsQuery = z.infer<typeof GetAlertsQuerySchema>;

/**
 * Alert provenance. `OCCUPANCY_THRESHOLD` is raised by `checkAndAlert`; the
 * rest are entered or ingested by a human or an upstream system, and carry no
 * occupancy reading.
 */
export const CapacityAlertTypeSchema = z.enum([
  'OCCUPANCY_THRESHOLD',
  'MANUAL',
  'EXTERNAL',
  'GATE_BLOCKED_DATE',
  'DEVICE_CAPACITY_DROP',
]);

export type CapacityAlertType = z.infer<typeof CapacityAlertTypeSchema>;

/**
 * Manually raise a capacity alert.
 *
 * The occupancy triple is optional because a manual alert is not a reading —
 * an operator raising "safety, west zone, gate crush" has no percentage to
 * quote. Threshold-fired alerts always carry it.
 */
export const CreateCapacityAlertSchema = z.object({
  type: CapacityAlertTypeSchema.optional().default('MANUAL'),
  level: z.string().min(1).max(50),
  message: z.string().min(1).max(1000),
  occupancy: z.number().int().nonnegative().optional(),
  maxCapacity: z.number().int().nonnegative().optional(),
  percentage: z.number().int().min(0).max(100).optional(),
  zoneId: z.string().nullable().optional(),
});

export type CreateCapacityAlert = z.infer<typeof CreateCapacityAlertSchema>;

export const AlertItemSchema = z.object({
  id: z.string(),
  type: z.string(),
  level: z.string(),
  // Nullable: manual and externally-ingested alerts carry no occupancy reading.
  percentage: z.number().nullable(),
  occupancy: z.number().int().nullable(),
  maxCapacity: z.number().int().nullable(),
  message: z.string().nullable(),
  zoneId: z.string().nullable(),
  acknowledged: z.boolean(),
  acknowledgedBy: z.string().nullable(),
  acknowledgedAt: z.string().nullable(),
  acknowledgedNote: z.string().nullable(),
  source: z.string(),
  createdAt: z.string(),
});

export type AlertItem = z.infer<typeof AlertItemSchema>;

/**
 * Cooldown response for a manual create that was suppressed.
 *
 * Shaped as a `status:false` envelope deliberately: the admin client's
 * `parseBackendResponse` throws `ApiError` for those but preserves the body on
 * `error.details.raw`, which is how `useCreateAlert` distinguishes a soft
 * "skipped" (yellow toast) from a real failure (destructive toast).
 */
export const CapacityAlertSkippedSchema = z.object({
  status: z.literal(false),
  code: z.literal(202),
  message: z.string(),
  skipped: z.literal(true),
  reason: z.string(),
});

export type CapacityAlertSkipped = z.infer<typeof CapacityAlertSkippedSchema>;

export const AlertsListResponseSchema = z.object({
  items: z.array(AlertItemSchema),
  meta: z.object({
    page: z.number().int(),
    limit: z.number().int(),
    total: z.number().int().nonnegative(),
    totalPages: z.number().int().nonnegative(),
    hasMore: z.boolean(),
  }),
});

export type AlertsListResponse = z.infer<typeof AlertsListResponseSchema>;

// ============================================================================
// Live Capacity Dashboard (Admin)
// ============================================================================

export const LiveParkOccupancySchema = z.object({
  currentOccupancy: z.number().int().nonnegative(),
  maxCapacity: z.number().int().positive(),
  occupancyPercentage: z.number().min(0).max(100),
  status: CapacityStatusSchema,
  peakOccupancy: z.number().int().nonnegative(),
  peakTime: z.string().nullable(),
  todayEntries: z.number().int().nonnegative(),
  todayExits: z.number().int().nonnegative(),
});

export type LiveParkOccupancy = z.infer<typeof LiveParkOccupancySchema>;

export const RateDataSchema = z.object({
  windowMinutes: z.number().int().positive(),
  entries: z.number().int().nonnegative(),
  exits: z.number().int().nonnegative(),
  netChange: z.number().int(),
  entryRatePerMinute: z.number(),
  exitRatePerMinute: z.number(),
});

export type RateData = z.infer<typeof RateDataSchema>;

export const GateBreakdownSchema = z.object({
  gateId: z.string(),
  gateName: z.string(),
  last15MinEntries: z.number().int().nonnegative(),
  last15MinExits: z.number().int().nonnegative(),
  todayEntries: z.number().int().nonnegative(),
  todayExits: z.number().int().nonnegative(),
  status: z.enum(['ACTIVE', 'INACTIVE', 'RESTRICTED']),
});

export type GateBreakdown = z.infer<typeof GateBreakdownSchema>;

export const LiveZoneOccupancySchema = z.object({
  zoneId: z.string(),
  zoneName: z.string(),
  currentOccupancy: z.number().int().nonnegative(),
  maxCapacity: z.number().int().positive(),
  occupancyPercentage: z.number().min(0).max(100),
  status: CapacityStatusSchema,
});

export type LiveZoneOccupancy = z.infer<typeof LiveZoneOccupancySchema>;

export const AlertSummarySchema = z.object({
  unacknowledgedCount: z.number().int().nonnegative(),
  latestAlerts: z.array(AlertItemSchema),
});

export type AlertSummary = z.infer<typeof AlertSummarySchema>;

export const LiveCapacitySnapshotSchema = z.object({
  park: LiveParkOccupancySchema,
  rates: RateDataSchema,
  gates: z.array(GateBreakdownSchema),
  zones: z.array(LiveZoneOccupancySchema),
  alerts: AlertSummarySchema,
  lastUpdated: z.string().datetime(),
});

export type LiveCapacitySnapshot = z.infer<typeof LiveCapacitySnapshotSchema>;

export const OccupancyUpdateEventSchema = z.object({
  eventType: z.literal('occupancy-update'),
  timestamp: z.string().datetime(),
  data: LiveCapacitySnapshotSchema,
});

export type OccupancyUpdateEvent = z.infer<typeof OccupancyUpdateEventSchema>;

export const LiveThresholdAlertEventSchema = z.object({
  eventType: z.literal('threshold-alert'),
  timestamp: z.string().datetime(),
  data: z.object({
    id: z.string(),
    type: z.string(),
    level: z.string(),
    thresholdPercentage: z.number(),
    currentPercentage: z.number(),
    occupancy: z.number().int(),
    maxCapacity: z.number().int(),
    zoneId: z.string().nullable(),
    message: z.string().nullable(),
  }),
});

export type LiveThresholdAlertEvent = z.infer<typeof LiveThresholdAlertEventSchema>;

// ============================================================================
// Reports & Snapshots (Admin)
// ============================================================================

export const CapacityReportQuerySchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
});

export type CapacityReportQuery = z.infer<typeof CapacityReportQuerySchema>;

// ============================================================================
// Attraction Calendar & Slot Generation (Admin)
// ============================================================================

export const AttractionDayCapacitySchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  totalCapacity: z.number().int().nonnegative(),
  reserved: z.number().int().nonnegative(),
  available: z.number().int().nonnegative(),
  hasSlots: z.boolean(),
});

export type AttractionDayCapacity = z.infer<typeof AttractionDayCapacitySchema>;

export const AttractionCalendarResponseSchema = z.object({
  attractionId: z.string(),
  year: z.number().int(),
  month: z.number().int().min(1).max(12),
  days: z.array(AttractionDayCapacitySchema),
});

export type AttractionCalendarResponse = z.infer<typeof AttractionCalendarResponseSchema>;

export const BulkSlotTemplateSchema = z.object({
  startTime: z.string().regex(/^\d{2}:\d{2}$/),
  endTime: z.string().regex(/^\d{2}:\d{2}$/),
  capacity: z.number().int().min(1),
});

export type BulkSlotTemplate = z.infer<typeof BulkSlotTemplateSchema>;

export const BulkGenerateAttractionSlotsSchema = z.object({
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  slots: z.array(BulkSlotTemplateSchema),
});

export type BulkGenerateAttractionSlots = z.infer<typeof BulkGenerateAttractionSlotsSchema>;

export const AutoGenerateAttractionSlotsSchema = z.object({
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  operatingStart: z.string().regex(/^\d{2}:\d{2}$/),
  operatingEnd: z.string().regex(/^\d{2}:\d{2}$/),
  slotDurationMin: z.number().int().min(15).max(240).optional().default(60),
  groupCapacityFactor: z.number().min(0.1).max(1.0).optional().default(0.8),
});

export type AutoGenerateAttractionSlots = z.infer<typeof AutoGenerateAttractionSlotsSchema>;

export const AttractionCapacityConfigSchema = z.object({
  attractionId: z.string(),
  cycleDurationMin: z.number().int().positive(),
  capacityPerCycle: z.number().int().positive(),
  updatedAt: z.string().datetime(),
});

export type AttractionCapacityConfig = z.infer<typeof AttractionCapacityConfigSchema>;

export const UpsertAttractionCapacityConfigSchema = z.object({
  cycleDurationMin: z.number().int().positive(),
  capacityPerCycle: z.number().int().positive(),
});

export type UpsertAttractionCapacityConfig = z.infer<typeof UpsertAttractionCapacityConfigSchema>;

export const BulkAttractionConfigItemSchema = z.object({
  attractionId: z.string().min(1),
  cycleDurationMin: z.number().int().positive(),
  capacityPerCycle: z.number().int().positive(),
});

export type BulkAttractionConfigItem = z.infer<typeof BulkAttractionConfigItemSchema>;

export const BulkUpsertAttractionConfigSchema = z.object({
  attractions: z.array(BulkAttractionConfigItemSchema),
});

export type BulkUpsertAttractionConfig = z.infer<typeof BulkUpsertAttractionConfigSchema>;

export const BulkAutoGenerateAllSlotsSchema = z.object({
  dateFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  dateTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  operatingStart: z.string().regex(/^\d{2}:\d{2}$/),
  operatingEnd: z.string().regex(/^\d{2}:\d{2}$/),
  slotDurationMin: z.number().int().min(15).max(240).optional(),
  groupCapacityFactor: z.number().min(0.1).max(1.0).optional(),
  attractionIds: z.array(z.string()).optional(),
});

export type BulkAutoGenerateAllSlots = z.infer<typeof BulkAutoGenerateAllSlotsSchema>;

export const BulkGenerateResponseSchema = z.object({
  datesProcessed: z.number().int().nonnegative(),
  slotsCreated: z.number().int().nonnegative(),
  datesSkipped: z.number().int().nonnegative(),
});

export type BulkGenerateResponse = z.infer<typeof BulkGenerateResponseSchema>;

export const BulkAttractionConfigResultSchema = z.object({
  attractionId: z.string(),
  status: z.enum(['success', 'error']),
  message: z.string().optional(),
});

export type BulkAttractionConfigResult = z.infer<typeof BulkAttractionConfigResultSchema>;

export const BulkConfigResponseSchema = z.object({
  total: z.number().int().nonnegative(),
  succeeded: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  results: z.array(BulkAttractionConfigResultSchema),
});

export type BulkConfigResponse = z.infer<typeof BulkConfigResponseSchema>;

export const BulkAutoGenerateResultSchema = z.object({
  attractionId: z.string(),
  status: z.enum(['success', 'error']),
  message: z.string().optional(),
  slotsCreated: z.number().int().nonnegative().optional(),
  datesSkipped: z.number().int().nonnegative().optional(),
});

export type BulkAutoGenerateResult = z.infer<typeof BulkAutoGenerateResultSchema>;

export const BulkAutoGenerateAllResponseSchema = z.object({
  total: z.number().int().nonnegative(),
  succeeded: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  totalSlotsCreated: z.number().int().nonnegative(),
  results: z.array(BulkAutoGenerateResultSchema),
});

export type BulkAutoGenerateAllResponse = z.infer<typeof BulkAutoGenerateAllResponseSchema>;

// ============================================================================
// Slot Management (Admin + Internal + Public)
// ============================================================================

export const GenerateSlotsSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  slots: z.array(z.object({
    startTime: z.string().regex(/^\d{2}:\d{2}$/),
    endTime: z.string().regex(/^\d{2}:\d{2}$/),
    capacity: z.number().int().min(1),
  })),
});

export type GenerateSlots = z.infer<typeof GenerateSlotsSchema>;

export const ReserveSlotSchema = z.object({
  slotId: z.string().min(1),
  wristbandId: z.string().min(1),
  visitorType: z.enum(['ADULT', 'CHILD', 'DISABLED', 'STUDENT', 'FOREIGNER']).optional(),
});

export type ReserveSlot = z.infer<typeof ReserveSlotSchema>;

export const RedeemReservationSchema = z.object({
  wristbandId: z.string().min(1),
  attractionId: z.string().min(1),
});

export type RedeemReservation = z.infer<typeof RedeemReservationSchema>;

export const SlotReservationItemSchema = z.object({
  slotId: z.string().min(1),
  quantity: z.number().int().min(1),
});

export type SlotReservationItem = z.infer<typeof SlotReservationItemSchema>;

export const ReserveGroupSlotsSchema = z.object({
  referenceId: z.string().min(1),
  slots: z.array(SlotReservationItemSchema),
});

export type ReserveGroupSlots = z.infer<typeof ReserveGroupSlotsSchema>;

export const VisitorTypeCountSchema = z.object({
  visitorType: z.enum(['ADULT', 'CHILD', 'DISABLED', 'STUDENT', 'FOREIGNER']),
  cap: z.number().int().min(0),
  reserved: z.number().int().min(0),
});

export type VisitorTypeCount = z.infer<typeof VisitorTypeCountSchema>;

export const MaterializeSlotItemSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  startTime: z.string().regex(/^\d{2}:\d{2}$/),
  endTime: z.string().regex(/^\d{2}:\d{2}$/),
  capacity: z.number().int().min(0),
  isUnlimited: z.boolean().optional(),
  scheduleId: z.string().optional(),
  visitorTypeCounts: z.array(VisitorTypeCountSchema).optional(),
});

export type MaterializeSlotItem = z.infer<typeof MaterializeSlotItemSchema>;

export const MaterializeBulkSchema = z.object({
  slots: z.array(MaterializeSlotItemSchema),
  closedDates: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).optional(),
});

export type MaterializeBulk = z.infer<typeof MaterializeBulkSchema>;

export const SlotSummaryQuerySchema = z.object({
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});

export type SlotSummaryQuery = z.infer<typeof SlotSummaryQuerySchema>;
